import {
    AlertIcon,
    notifyWithAlertOrToast,
} from "../../components/global/alert/Alert";
// tslint:disable-next-line:no-submodule-imports
import { all, put, select, takeEvery } from "redux-saga/effects";
import {
    LOG_ESTIMATION_TO_CONSOLE,
    PROPERTIES_DISPATCH_ESTIMATION_USE_CACHE,
    PROPERTIES_ESTIMATION_SHOW_ALERT_ERRORS,
} from "../../components/utilities/FeatureFlags";
import { PROPERTIES_DISPATCH_ESTIMATION_ENABLED } from "../../components/utilities/FeatureGates";
import ViridisUtils from "../../components/utilities/ViridisUtils";
import { IChemicalModel } from "../../data/ChemicalModel";
import t from "../../i18n/i18next";
import { chemicalInfoModalEstimateCompound } from "../actions/chemicalInfoModal";
import {
    estimateCompound,
    loadCompounds,
    saveCurrentSearch,
    searchCompounds,
} from "../actions/pocketbook";
import { getUserLicenseInfo } from "../actions/user";
import { ILoadCompoundsResponse } from "../reducers/pocketbook";
import { IUserState } from "../types/IUserState";
import { resolveRsaaErrorMessage } from "../utils/rsaaErrorMessage";

interface EstimationResponse {
    models: IChemicalModel[];
    taskClass: string;
    totalSeconds: number;
}

type ILoadCompoundsSuccessAction = {
    type?: string;
    payload: ILoadCompoundsResponse;
    meta?: {
        usesDataRequestEstimation?: boolean;
    };
};

function* logEstimationResults(action) {
    if (!LOG_ESTIMATION_TO_CONSOLE()) {
        return;
    }
    const response: EstimationResponse = action.payload;
    const guid = response.models[0].guid;
    const compoundName = response.models[0].name;
    const taskClass = response.taskClass;
    const elapsedSeconds = response.totalSeconds;
    // tslint:disable-next-line no-console
    ViridisUtils.log(
        `Estimation: ${guid} ${compoundName} ${taskClass} ${elapsedSeconds}s`
    );

    yield null;
}

export function* dispatchSearchEstimation(action: ILoadCompoundsSuccessAction) {
    const loadCompoundsAction = action;

    if (loadCompoundsAction.meta?.usesDataRequestEstimation) {
        return;
    }

    yield dispatchGetUserInfo();

    const user: IUserState = yield select((state) => state.user);
    if (!PROPERTIES_DISPATCH_ESTIMATION_ENABLED(user)) {
        return;
    }

    const response: ILoadCompoundsResponse = loadCompoundsAction.payload;

    yield all(
        (response.models || []).map((m) =>
            put(
                estimateCompound.action({
                    guid: m.guid,
                    compoundId: m.id,
                    name: m.name,
                    smiles: m.smiles,
                    type: "Pocketbook",
                    clearCompounds: false,
                    useCache: PROPERTIES_DISPATCH_ESTIMATION_USE_CACHE(),
                })
            )
        )
    );
}

function* dispatchGetUserInfo() {
    yield put(getUserLicenseInfo.action());
}

export function* estimationAlertError(action) {
    if (!PROPERTIES_ESTIMATION_SHOW_ALERT_ERRORS()) {
        return;
    }
    const response: EstimationResponse = action.payload;
    const model = response.models?.[0];
    const identity = [model?.guid, model?.name].filter(Boolean).join(" ");
    const prefix = identity ? `${identity}: ` : "";
    yield notifyWithAlertOrToast(
        `${prefix}${t("ESTIMATE_COMPOUND_ERROR")}`,
        undefined,
        AlertIcon.ERROR
    );
}

function* responseAlertError(action: { payload: unknown }) {
    yield notifyWithAlertOrToast(
        t(resolveRsaaErrorMessage(action.payload, "Request failed")),
        undefined,
        AlertIcon.ERROR
    );
}

function* saveSearchSuccess() {
    notifyWithAlertOrToast(
        t("redux.saveSearchSuccess.yourResultsHaveBeen"),
        t("redux.saveSearchSuccess.resultsSaved"),
        AlertIcon.SUCCESS
    );
}

function* pocketbookSaga() {
    yield all([
        takeEvery(searchCompounds.error, responseAlertError),
        takeEvery(estimateCompound.request, dispatchGetUserInfo),
        takeEvery(estimateCompound.update, logEstimationResults),
        takeEvery(estimateCompound.error, estimationAlertError),
        takeEvery(
            chemicalInfoModalEstimateCompound.error,
            estimationAlertError
        ),
        takeEvery(loadCompounds.success, dispatchSearchEstimation),
        takeEvery(loadCompounds.error, responseAlertError),
        takeEvery(saveCurrentSearch.success, saveSearchSuccess),
    ]);
}

export default pocketbookSaga;
